import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { faBan, faCheck, faCircleInfo, faEllipsisVertical, faEye, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CustomColDef } from '@shared/components/ui/AgGridTable';
import { usePermissions } from '@shared/hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BankAccount, BankAccountStatus } from '../models/BankAccount';

// Formatear fecha
const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

// Ocultar parcialmente el IBAN
const maskIban = (iban: string) => {
  if (!iban || iban.length < 8) return iban;
  const visible = iban.slice(0, 4) + '...' + iban.slice(-4);
  return visible;
};

// Obtener badge según estado
const getStatusBadge = (status: BankAccountStatus, t: TFunction<'bankAccountsModule'>) => {
  const statusConfig: Record<
    BankAccountStatus,
    {
      variant: 'default' | 'destructive' | 'secondary' | 'outline' | 'transparent-green' | 'transparent-orange';
      label: string;
    }
  > = {
    PENDING: { variant: 'transparent-orange', label: t('status.pending') },
    VERIFIED: { variant: 'transparent-green', label: t('status.verified') },
    REJECTED: { variant: 'destructive', label: t('status.rejected') },
    DISABLED: { variant: 'secondary', label: t('status.disabled') },
  };

  const config = statusConfig[status] || statusConfig.PENDING;

  return (
    <Badge variant={config.variant} className="text-xs">
      {config.label}
    </Badge>
  );
};

export type BankAccountsColumnDef = ColumnDef<BankAccount>;

export interface UseBankAccountsColumnsOptions {
  onVerify?: (account: BankAccount) => void;
  onReject?: (account: BankAccount) => void;
  onDisable?: (account: BankAccount) => void;
  onViewDocument?: (account: BankAccount) => void;
  /** Solo cuentas VERIFIED: abre modal de detalle */
  onViewDetails?: (account: BankAccount) => void;
  selectedColumns?: string[];
}

type BuildBankAccountsColumns = (
  t: TFunction<'bankAccountsModule'>,
  options: UseBankAccountsColumnsOptions,
  is3pl: boolean,
  canVerify: boolean,
) => BankAccountsColumnDef[];

const buildBankAccountsColumns: BuildBankAccountsColumns = (t, options, is3pl, canVerify) => {
  const { onVerify, onReject, onDisable, onViewDocument, onViewDetails } = options;

  const columns: BankAccountsColumnDef[] = [
    {
      id: 'holder_name',
      accessorKey: 'holder_name',
      header: t('table.columns.holder_name'),
      cell: ({ row }) => <span className="text-sm font-medium">{row.original.holder_name}</span>,
    },
    {
      id: 'iban',
      accessorKey: 'iban',
      header: t('table.columns.iban'),
      cell: ({ row }) => <span className="text-sm font-mono">{maskIban(row.original.iban)}</span>,
    },
    {
      id: 'bic',
      accessorKey: 'bic',
      header: t('table.columns.bic'),
      cell: ({ row }) => <span className="text-sm font-mono">{row.original.bic}</span>,
    },
    {
      id: 'bank_name',
      accessorKey: 'bank_name',
      header: t('table.columns.bank_name'),
      cell: ({ row }) => <span className="text-sm">{row.original.bank_name}</span>,
    },
    {
      id: 'country_code',
      accessorKey: 'country_code',
      header: t('table.columns.country'),
      cell: ({ row }) => <span className="text-sm">{row.original.country_code}</span>,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('table.columns.status'),
      cell: ({ row }) => getStatusBadge(row.original.status, t),
    },
    {
      id: 'team',
      accessorKey: 'team_id',
      header: t('table.columns.team'),
      cell: ({ row }) => <span className="text-sm">{row.original.team?.name ?? `#${row.original.team_id}`}</span>,
    },
  ];

  // Columnas adicionales solo para 3PL
  if (is3pl) {
    columns.push({
      id: 'verified_at',
      accessorKey: 'verified_at',
      header: t('table.columns.verified_at'),
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.verified_at)}</span>,
    });
    columns.push({
      id: 'verified_by',
      accessorKey: 'verified_by_user_id',
      header: t('table.columns.verified_by'),
      cell: ({ row }) => {
        const account = row.original;
        if (account.verified_by_user) {
          return (
            <span className="text-sm">
              {account.verified_by_user.name} {account.verified_by_user_id && `(#${account.verified_by_user_id})`}
            </span>
          );
        }
        if (account.verified_by_user_id) {
          return <span className="text-sm">#{account.verified_by_user_id}</span>;
        }
        return <span className="text-sm">—</span>;
      },
    });
  }

  // Columna de acciones
  columns.push({
    id: 'actions',
    header: '',
    ...({ minWidth: 50 } as any),
    enableSorting: false,
    cell: ({ row }) => {
      const account = row.original;
      const isPending = account.status === 'PENDING';
      const isVerified = account.status === 'VERIFIED';

      const actionItems: ReactNode[] = [];

      if (isVerified && onViewDetails) {
        actionItems.push(
          <DropdownMenuItem key="view-details" onClick={() => onViewDetails(account)}>
            <FontAwesomeIcon icon={faCircleInfo} className="mr-2 h-4 w-4" />
            {t('actions.viewDetails')}
          </DropdownMenuItem>,
        );
      }

      if (account.proof_document_path) {
        actionItems.push(
          <DropdownMenuItem key="view-document" onClick={() => onViewDocument?.(account)}>
            <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-2" />
            {t('actions.viewDocument')}
          </DropdownMenuItem>,
        );
      }

      if (is3pl && isPending && canVerify) {
        actionItems.push(
          <DropdownMenuItem key="verify" onClick={() => onVerify?.(account)}>
            <FontAwesomeIcon icon={faCheck} className="w-4 h-4 mr-2 text-green-600" />
            {t('actions.verify')}
          </DropdownMenuItem>,
        );

        actionItems.push(
          <DropdownMenuItem key="reject" onClick={() => onReject?.(account)} className="text-destructive">
            <FontAwesomeIcon icon={faTimes} className="w-4 h-4 mr-2" />
            {t('actions.reject')}
          </DropdownMenuItem>,
        );
      }

      if (isVerified) {
        actionItems.push(
          <DropdownMenuItem key="disable" onClick={() => onDisable?.(account)}>
            <FontAwesomeIcon icon={faBan} className="w-4 h-4 mr-2" />
            {t('actions.disable')}
          </DropdownMenuItem>,
        );
      }

      const hasActions = actionItems.length > 0;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 px-0" aria-label={t('table.columns.actions')}>
              <FontAwesomeIcon icon={faEllipsisVertical} className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            {hasActions ? actionItems : <DropdownMenuItem disabled>{t('noResults')}</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  });

  return columns;
};

const mapToAgGridColumns = (columns: BankAccountsColumnDef[]): CustomColDef[] => {
  return columns.map((column) => {
    const accessorKey =
      'accessorKey' in column && typeof column.accessorKey === 'string' ? column.accessorKey : undefined;

    const columnId = (column.id as string) || accessorKey || String((column as any).accessorKey ?? '');

    const isActionsColumn = columnId === 'actions';

    return {
      colId: columnId,
      field: accessorKey && !isActionsColumn ? accessorKey : undefined,
      headerName: typeof column.header === 'string' ? column.header : undefined,
      pinned: isActionsColumn ? 'right' : undefined,
      width: column.size ? column.size : undefined,
      minWidth: (column as any).minWidth,
      maxWidth: (column as any).maxWidth,
      sortable: column.enableSorting !== false,
      lockPosition: isActionsColumn ? 'right' : undefined,
      suppressMovable: isActionsColumn,
      suppressAutoSize: isActionsColumn,
      suppressSizeToFit: isActionsColumn,
      cellRenderer: (params: any) => {
        if (typeof column.cell !== 'function') {
          return params.value;
        }

        return column.cell({
          row: { original: params.data },
          getValue: () => params.value,
        } as any);
      },
    } as CustomColDef;
  });
};

export interface UseBankAccountsColumnsResult {
  tanStackColumns: BankAccountsColumnDef[];
  agGridColumnDefs: CustomColDef[];
  filteredColumnDefs: CustomColDef[];
}

export const useBankAccountsColumns = (options?: UseBankAccountsColumnsOptions): UseBankAccountsColumnsResult => {
  const { t } = useTranslation('bankAccountsModule');
  const { canAccess } = usePermissions();
  const is3pl = canAccess(['tenant_3pl'], []);
  const canVerify = canAccess(['tenant_3pl'], ['bank-accounts.verify']);

  const tanStackColumns = useMemo<BankAccountsColumnDef[]>(
    () => buildBankAccountsColumns(t, options ?? {}, is3pl, canVerify),
    [t, options, is3pl, canVerify],
  );

  const agGridColumnDefs = useMemo(() => mapToAgGridColumns(tanStackColumns), [tanStackColumns]);

  const filteredColumnDefs = useMemo(() => {
    const selectedIds = options?.selectedColumns;
    if (!selectedIds || selectedIds.length === 0) {
      return agGridColumnDefs;
    }

    return agGridColumnDefs.filter((column) => selectedIds.includes(String(column.colId)));
  }, [agGridColumnDefs, options?.selectedColumns]);

  return {
    tanStackColumns,
    agGridColumnDefs,
    filteredColumnDefs,
  };
};
